import React, { useState } from 'react';
import { CheckCircle, User, Users } from 'lucide-react';
import './ModifiedDutyApprovals.css';
import { approveSupervisorSlot } from '../../api/timesheet';
import type { RequiredSuperSlot } from '../../interfaces/EmployeeInterface';
import { approvalErrorMessage } from '../../util/slotApprovals';
import {
  isWaitingOnAnotherApprover, onBehalfModifiedDutySlot, superSlotProgress, superSlotSummaryText, waitingApprovalText,
} from '../../util/modifiedDutyApprovals';

/**
 * WCDP Modified Duty second approver (TAS-679) — the review console's view of a timesheet
 * that needs BOTH the supervisor and HR (the Modified Duty approver), in any order.
 *
 * Deliberately separate from the console's NPM `allowManagerApproval` slot UI (SlotRow /
 * runSlotApprovals): the console renders these only when the payload carries
 * `requiredSupers` with 2+ slots, which the API sends only for WCDP with its gate on.
 * Styles reuse the console's .etrc-apprsec / .etrc-slot vocabulary.
 */

/** Header status-row line: "Supervisor approvals 1 of 2 · waiting on Adelina Estrada". */
export function ModifiedDutyApprovalLine({ slots }: { slots: RequiredSuperSlot[] }) {
  const p = superSlotProgress(slots);
  const done = p.approved >= p.total;
  return (
    <span className={`etrc-md-line${done ? ' ok' : ''}`} title="Modified Duty: HR approves in addition to the supervisor">
      <Users size={14} />
      {superSlotSummaryText(p)}
    </span>
  );
}

export interface ModifiedDutyApprovalsSectionProps {
  timesheetID: number;
  slots: RequiredSuperSlot[];
  /** The SIGNED-IN admin — recorded as the approver (never the slot owner's ID). */
  adminEmployeeID?: number;
  adminName?: string;
  /** admin || payroll || payrollApproveTimesheets — the API 403s anyone else. */
  canApproveOnBehalf: boolean;
  /** The timesheet is Submitted and loaded — the only stage a slot can be filled in. */
  actionable: boolean;
  /** Approval stamp for a filled slot (the console's formatter, with the row's offset). */
  renderStamp: (slot: RequiredSuperSlot) => React.ReactNode;
  /** Slot filled, timesheet still Submitted (another slot open). */
  onWaiting: (message: string) => void;
  /** Slot filled and it was the last — the timesheet moved on. */
  onAdvanced: (message: string) => void;
}

export function ModifiedDutyApprovalsSection({
  timesheetID, slots, adminEmployeeID, adminName, canApproveOnBehalf, actionable,
  renderStamp, onWaiting, onAdvanced,
}: ModifiedDutyApprovalsSectionProps) {
  const [target, setTarget] = useState<RequiredSuperSlot | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const p = superSlotProgress(slots);
  const behalfSlot = onBehalfModifiedDutySlot(slots);

  const approve = async (slot: RequiredSuperSlot) => {
    setTarget(null);
    if (busy) return;
    if (!adminEmployeeID) {
      setError('Your admin account could not be identified — sign out and back in.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await approveSupervisorSlot(timesheetID, adminEmployeeID, slot.requiredSuperApprovalsID);
      if (res && res.success === false) {
        setError(res.errorMessage || 'Approval was rejected.');
        return;
      }
      if (isWaitingOnAnotherApprover(res)) {
        onWaiting(waitingApprovalText(res, slot.name));
      } else {
        onAdvanced(`Approved on behalf of ${slot.name} — all supervisor approvals complete.`);
      }
    } catch (err) {
      setError(approvalErrorMessage(err) || 'Approval failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="etrc-apprsec etrc-md-sec">
      <div className="hd">
        <CheckCircle size={15} />Supervisor approvals
        <span className="etrc-md-tag">Modified Duty</span>
        <span className={`cnt${p.approved >= p.total ? ' ok' : ''}`}>{p.approved} of {p.total} complete</span>
      </div>
      <div className="etrc-slotlist">
        {slots.map((s) => {
          if (s.approved) {
            const behalf = !!s.approvedBy && s.approvedBy.trim().toLowerCase() !== s.name.trim().toLowerCase();
            return (
              <div key={s.requiredSuperApprovalsID} className="etrc-slot done">
                <span className="etrc-slot-mark"><CheckCircle size={20} /></span>
                <span className="etrc-slot-name">
                  {s.name}
                  {s.isModifiedDutyApprover && <span className="etrc-md-tag">HR</span>}
                </span>
                <span className="etrc-slot-meta">
                  Approved by <b>{s.approvedBy || '—'}</b>
                  {behalf && <> on behalf of <b>{s.name}</b></>}
                  {s.approvalDateTime && <> · {renderStamp(s)}</>}
                </span>
              </div>
            );
          }
          const showBehalf = actionable && canApproveOnBehalf
            && behalfSlot?.requiredSuperApprovalsID === s.requiredSuperApprovalsID;
          return (
            <div key={s.requiredSuperApprovalsID} className="etrc-slot">
              <span className="etrc-slot-mark" />
              <span className="etrc-slot-name">
                {s.name}
                {s.isModifiedDutyApprover && <span className="etrc-md-tag">HR</span>}
              </span>
              {showBehalf ? (
                <button
                  type="button"
                  className="etrc-btn small etrc-slot-action"
                  disabled={busy}
                  onClick={() => setTarget(s)}
                >
                  {/* Label stays fixed while busy (disabled only) so the button never resizes. */}
                  <User size={13} />Approve on behalf of {s.name}
                </button>
              ) : (
                <span className="etrc-slot-await">Pending</span>
              )}
            </div>
          );
        })}
      </div>
      {/* Always rendered: the line's height is reserved so an error appearing (or
          clearing) never moves the card below it. */}
      <div className={`etrc-md-status${error ? ' err' : ''}`} role="status" aria-live="polite">
        {error ?? (busy ? 'Approving…' : ' ')}
      </div>

      {target && (
        <div className="etrc-overlay" onClick={() => setTarget(null)}>
          <div className="etrc-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="etrc-confirm-header">
              <User size={20} color="#2f6fed" />
              <span>Approve on behalf of {target.name}?</span>
            </div>
            <p className="etrc-confirm-body">
              This is <b>{target.name}</b>’s Modified Duty approval. You are{' '}
              <b>{adminName || 'the signed-in admin'}</b> — it will be recorded as{' '}
              <b>approved by {adminName || 'you'} on behalf of {target.name}</b> and cannot be
              undone without unlocking the week.
            </p>
            <div className="etrc-confirm-actions">
              <button className="etrc-btn" onClick={() => setTarget(null)} disabled={busy}>
                Cancel
              </button>
              <button className="etrc-btn primary" onClick={() => { void approve(target); }} disabled={busy}>
                <CheckCircle size={15} />Approve on behalf
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
